/**
 * Hybrid V1 <-> V2 request/response adapter (dtrader-c mirror of PU's adapter).
 *
 *   - When `auth_flow === 'oauth2'` rewrite outbound legacy-shaped requests so the V2 OTP WebSocket
 *     accepts them, and normalise inbound V2 messages back to legacy shape downstream code expects.
 *   - When `auth_flow === 'legacy'` both functions are no-ops.
 *
 * The single highest-impact alias is `proposal_open_contract` (POC) tick fields: V2 omits
 * `entry_tick_display_value`/`exit_tick_display_value` and only ships numeric `entry_spot`/`exit_spot`/
 * `sell_spot`. We back-fill the legacy display fields here so trader components keep rendering.
 */

import { getAuthFlow } from '@deriv/utils';

type TPlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is TPlainObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const aliasProposalOpenContractTickDisplay = (obj: TPlainObject) => {
    const isMissing = (key: string) =>
        !(key in obj) || obj[key] === undefined || obj[key] === null || obj[key] === '';

    if (
        isMissing('entry_tick_display_value') &&
        'entry_spot' in obj &&
        obj.entry_spot != null &&
        String(obj.entry_spot) !== ''
    ) {
        obj.entry_tick_display_value = String(obj.entry_spot);
    }

    if (isMissing('exit_tick_display_value')) {
        const exitSrc =
            ('exit_spot' in obj && obj.exit_spot != null ? obj.exit_spot : undefined) ??
            ('sell_spot' in obj && obj.sell_spot != null ? obj.sell_spot : undefined);
        if (exitSrc !== undefined && String(exitSrc) !== '') {
            obj.exit_tick_display_value = String(exitSrc);
        }
    }
};

export const adaptRequestForActiveFlow = (request: unknown): unknown => {
    if (getAuthFlow() !== 'oauth2') return request;
    return request;
};

export const adaptResponseForActiveFlow = (response: unknown): unknown => {
    if (getAuthFlow() !== 'oauth2') return response;
    if (!isPlainObject(response)) return response;

    const poc = response.proposal_open_contract;
    if (isPlainObject(poc)) {
        aliasProposalOpenContractTickDisplay(poc);
    }

    return response;
};
